import type Phaser from 'phaser';
import { audio } from '../audio/engine';

/**
 * Tippen statt Druecken.
 *
 * Bis hierher loesten alle Schaltflaechen beim *Herunterdruecken* aus. Mit der
 * Maus merkt man das kaum; auf einem Touchscreen heisst es, dass jeder
 * Versuch zu scrollen, der zufaellig auf einer Zeile beginnt, diese Zeile
 * waehlt. Und nach einem Szenenwechsel auf `pointerdown` landete das
 * zugehoerige `pointerup` auf dem, was in der neuen Szene an derselben Stelle
 * lag.
 *
 * Jetzt zaehlt: Der Druck beginnt auf dem Objekt, endet auf ihm, und
 * dazwischen wurde nicht gezogen. Das ist, was Betriebssysteme unter einem
 * Klick verstehen.
 */

/** Wurde seit dem letzten Aufsetzen gezogen? */
let dragged = false;

/** Von Scrollbereichen gerufen: Das laufende Druecken ist ein Ziehen. */
export function noteDrag(): void {
  dragged = true;
}

export interface TapHandlers {
  readonly onTap: () => void;
  /** Hervorheben und Zuruecksetzen — Maus-Hover und Finger-Druck zugleich. */
  readonly onHighlight?: (on: boolean) => void;
}

/**
 * Verdrahtet ein interaktives Objekt als Schaltflaeche.
 *
 * Die Hervorhebung wird auch beim Loslassen zurueckgenommen: Auf einem
 * Touchscreen gibt es kein `pointerout`, bevor der naechste Finger irgendwo
 * anders aufsetzt — ohne das blieb eine angetippte Schaltflaeche hell stehen.
 */
export function onTap(object: Phaser.GameObjects.GameObject, handlers: TapHandlers): void {
  let pressed = false;
  const highlight = (on: boolean): void => handlers.onHighlight?.(on);

  object.on('pointerover', () => highlight(true));
  object.on('pointerout', () => {
    pressed = false;
    highlight(false);
  });
  object.on('pointerdown', () => {
    // Browser lassen Audio erst nach einer Nutzerhandlung zu — jede Bedienung
    // weckt den Ton, statt sich auf einen einzelnen Handler zu verlassen.
    audio.resume();
    pressed = true;
    dragged = false;
    highlight(true);
  });
  object.on('pointerup', (pointer: Phaser.Input.Pointer) => {
    const fire = pressed && !dragged;
    pressed = false;
    if (pointer.wasTouch) highlight(false);
    if (fire) handlers.onTap();
  });
}
